import { API_BASE } from './config';
import { getInitData } from './tg';

// ───── HTTP-утилиты ─────

export class ApiError extends Error {
  constructor(status, detail, code = null, body = null) {
    super(typeof detail === 'string' ? detail : JSON.stringify(detail));
    this.status = status;
    this.detail = detail;
    // Машиночитаемый код ошибки от бэка (см. поле `code` в теле ответа):
    // 'user_blocked' — пользователь забанен, initdata-* — проблемы с авторизацией,
    // ACCESS_CODES.* — не куплено или кончилось (402).
    this.code = code;
    // Тело ответа целиком: у 402 в нём приезжает ещё и `balance`, чтобы экран мог
    // показать «осталось 0» тем же кодом, что и после удачного ответа.
    this.body = body;
    this.name = 'ApiError';
  }
}

// ───── ДОСТУП И ОПЛАТА ─────

// Коды 402 (backend/core/errors.py, класс AccessCode). Бэк отвечает 402, когда
// действие упирается не в права, а в покупку: 403 в этом приложении занят баном,
// и путать их нельзя — забаненному показывают экран блокировки, а этому витрину.
export const ACCESS_CODES = {
  TAROT: 'tarot_subscription_required',
  ASTRO: 'astro_package_required',
  READINGS: 'readings_quota_exhausted',
  SPREADS: 'tarot_spreads_exhausted',
  CHANNEL: 'channel_subscription_required',
  MESSAGES: 'ai_messages_exhausted',
};

export function isAccessError(error) {
  return error instanceof ApiError && error.status === 402;
}

// Текст для экрана «нужна покупка» — по коду, а не по detail с бэка: так копирайт
// правится на фронте и не зависит от формулировок сервера.
const ACCESS_COPY = {
  [ACCESS_CODES.TAROT]: {
    title: 'Нужна подписка на Таро',
    text: 'Этот расклад входит в подписку. Оформи её — откроются все расклады каталога и вопросы к ИИ.',
  },
  [ACCESS_CODES.ASTRO]: {
    title: 'Нужен астро-пакет',
    text: 'Этот разбор входит в пакет Формулы души. Выбери подходящий блок — и разбор откроется.',
  },
  [ACCESS_CODES.READINGS]: {
    title: 'Разборы в пакете закончились',
    text: 'Ты использовала все разборы из своего пакета. Купи ещё один, чтобы продолжить.',
  },
  // Отдельно от READINGS: там кончился купленный пакет разборов и логично предложить
  // добрать ещё один, здесь — израсходован бесплатный расклад, и предлагать надо подписку.
  [ACCESS_CODES.SPREADS]: {
    title: 'Бесплатный расклад использован',
    text: 'Пробный расклад уже сделан. Оформи подписку на Таро — откроются все расклады каталога и вопросы к ИИ.',
  },
  // Единственный код, который снимается не покупкой, а подпиской. В теле ответа рядом
  // с ним приезжает `channel` — адрес канала, по нему экран и рисует кнопку «Перейти».
  [ACCESS_CODES.CHANNEL]: {
    title: 'Нужна подписка на канал',
    text: 'Бесплатный расклад открывается подписчикам канала. Подпишись и нажми «Забрать» ещё раз.',
  },
  [ACCESS_CODES.MESSAGES]: {
    title: 'Закончились вопросы к ИИ',
    text: 'Вопросы к ИИ тратятся на любой запрос — по раскладу, по разбору и в свободном чате. Докупи пакет вопросов.',
  },
};

export function accessCopy(error) {
  return ACCESS_COPY[error?.code] || {
    title: 'Нужна покупка',
    text: error?.detail || 'Чтобы продолжить, оформи подходящий тариф.',
  };
}

// GET /api/v1/users/me/access → {has_tarot, has_astrology, subscription_until, readings_left,
//   subscription_days_left, subscription_expired_at,
//   balance:{free,paid,total,by_scope,wallets:[{scope,free,paid,total}]},
//   entitlements:[{id,kind,scope,source,tariff_id,tariff_code,tariff_title,tariff_subtitle,
//     duration_days,starts_at,expires_at,readings_quota,readings_used,readings_left,is_exhausted}],
//   spread_ids[], reading_ids[]}
// Единственный источник правды о доступе: замки в каталоге, счётчик вопросов в чате
// и карточки профиля рисуются отсюда. Клиент ничего не складывает и сроки не сверяет —
// всё уже посчитано на сервере на момент запроса: subscription_until — до какой даты
// жива подписка Таро (null при бессрочной, различает has_tarot), readings_left — сколько
// разборов осталось по всем астро-пакетам вместе (null — пакет без лимита).
// subscription_days_left — сколько дней подписке осталось (вверх, у живой не бывает 0),
// subscription_expired_at — когда она сгорела; непусто только при has_tarot=false и только
// если подписка была, поэтому им и различаются «кончилась» (предложить продлить) и
// «не покупал» (предложить оформить) — сам has_tarot=false их не различает.
// У каждой покупки приложен снимок тарифа, поэтому профиль показывает её название из
// каталога, а не догадывается по kind.
export async function getAccess() {
  return apiFetch('/api/v1/users/me/access');
}

// Бэк отвечает этим кодом на любой запрос забаненного пользователя (403,
// backend/middlewares/ban.py). Экран блокировки один на всё приложение, а поймать
// такой ответ может любой запрос, поэтому вместо проброса через все вызовы
// отправляем событие: App слушает его и показывает экран блокировки.
export const BLOCKED_CODE = 'user_blocked';
export const BLOCKED_EVENT = 'taro:blocked';

export function isBlockedError(error) {
  return error instanceof ApiError && error.status === 403 && error.code === BLOCKED_CODE;
}

function authHeaders() {
  const initData = getInitData();
  const headers = { 'Content-Type': 'application/json' };
  if (initData) headers['Authorization'] = `tma ${initData}`;
  return headers;
}

// Таймаут сетевых запросов; генерация AI-чтения дольше — там свой лимит
const FETCH_TIMEOUT_MS = 12000;
const GENERATE_TIMEOUT_MS = 90000;

// Экспортируется для админки (frontend/src/admin/api.js): те же заголовки и та же
// обработка ошибок, но список эндпоинтов админки живёт отдельно от клиентского API.
export async function apiFetch(path, options = {}) {
  const timeout = options.timeoutMs ?? FETCH_TIMEOUT_MS;
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    signal: AbortSignal.timeout(timeout),
    headers: { ...authHeaders(), ...options.headers },
  });
  if (res.status === 204) return null;
  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: res.statusText }));
    const error = new ApiError(res.status, body.detail ?? 'Unknown error', body.code ?? null, body);
    if (isBlockedError(error)) dispatchEvent(new CustomEvent(BLOCKED_EVENT, { detail: error.detail }));
    throw error;
  }
  return res.json();
}

export async function apiDownload(path, filename) {
  const res = await fetch(`${API_BASE}${path}`, {
    signal: AbortSignal.timeout(60000),
    headers: authHeaders(),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: res.statusText }));
    throw new ApiError(res.status, body.detail ?? 'Download failed', body.code ?? null, body);
  }
  const url = URL.createObjectURL(await res.blob());
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

// ───── HEALTH ─────

// GET /health → {status, db}
export async function checkHealth() {
  return apiFetch('/health');
}

// ───── ПОЛЬЗОВАТЕЛИ ─────

// POST /api/v1/users/me → регистрация (идемпотентно), identity из initData.
// Запасной путь регистрации: бот заводит пользователя на /start, а этот вызов —
// при первом открытии мини-аппы, если до бота дело не дошло.
// referrer — код приглашения вида `ref_<id>` (start_param ссылки). Бэк всё равно
// предпочитает тот же параметр из подписанной initData, это лишь дубль на случай,
// если клиент открыт по ссылке, которую Telegram не прокинул в initData.
export async function registerUser(referrer = null) {
  return apiFetch('/api/v1/users/me', {
    method: 'POST',
    body: JSON.stringify({ referrer }),
  });
}

// GET /api/v1/users/me/referrals → {code, link, count, referrer}
// link — готовая ссылка `https://t.me/<бот>?start=ref_<мой id>` (null, если бэк не
// смог узнать username бота), count — сколько человек по ней зарегистрировалось.
export async function getReferrals() {
  return apiFetch('/api/v1/users/me/referrals');
}

// GET /api/v1/users/me → профиль текущего пользователя
export async function getMe() {
  return apiFetch('/api/v1/users/me');
}

// Кошелёк с балансом в рублях и «покупка доступа за деньги со счёта» отсюда убраны:
// таких эндпоинтов на бэке нет и не было (вызовы всегда падали в 404), а доступ
// теперь описывается покупками — см. getAccess() выше. Реальная оплата появится
// отдельно; до неё покупка оформляется на витрине тарифов.

// ───── ТАРИФЫ ─────

// GET /api/v1/tariffs → [{id, kind, code, title, subtitle, description, currency,
//   price, full_price, compare_at_price, discount_percent, duration_days,
//   readings_quota, ai_messages, bonus_messages, total_messages, ...}]
// Цена приходит уже посчитанной под регион пользователя: бэк берёт регион из
// подписанной initData (language_code), применяет скидку тарифа и региональную
// наценку. Клиенту остаётся только отрисовать число — никакой арифметики.
// kind: 'tarot_subscription' | 'astro_package' | 'message_pack'.
export async function fetchTariffs(kind = null) {
  const q = kind ? `?kind=${encodeURIComponent(kind)}` : '';
  return apiFetch(`/api/v1/tariffs${q}`);
}

// GET /api/v1/tariffs/{id} → тариф + spreads[] и readings[], которые он открывает
// (флаги «входит всё» и исключения бэк уже развернул в конкретные строки каталога).
export async function fetchTariff(tariffId) {
  return apiFetch(`/api/v1/tariffs/${tariffId}`);
}

// GET /api/v1/tariffs/trial → {available, activated, activated_at, readings_left, tariff, channel}
// Пробный тариф: бесплатный расклад и вопрос к ИИ, один раз на человека. Решение
// «показывать ли всплывашку» целиком в этих двух флагах — available (подарок заведён и
// включён в админке) и activated (этот человек его уже забирал, когда угодно). Клиент
// ничего не досчитывает и в localStorage ответ не кеширует: выключить предложение
// должно быть можно одним чекбоксом в админке.
export async function getTrialTariff() {
  return apiFetch('/api/v1/tariffs/trial');
}

// POST /api/v1/tariffs/trial/activate → тот же TrialStatus, уже с activated=true.
// Кассы здесь нет: тариф стоит 0 ₽ и выдаётся сразу, поэтому после успеха достаточно
// перечитать getAccess(). 409 — уже забирал (например, второй вкладкой), 404 — подарок
// сняли из админки, пока экран был открыт; и то и другое лечится перечитыванием статуса.
// 402 с кодом ACCESS_CODES.CHANNEL — не подписан на канал: в теле приезжает `channel`,
// человека надо увести туда и дать нажать «Забрать» ещё раз. 503 — подписку не удалось
// проверить (Telegram молчит или у бота сняли админку в канале), помогает только повтор.
export async function activateTrialTariff() {
  return apiFetch('/api/v1/tariffs/trial/activate', { method: 'POST' });
}

// ───── ОПЛАТА (Продамус) ─────

// POST /api/v1/payments/prodamus {tariff_id} →
//   {payment_id, url, amount, currency, status, tariff_id, tariff_title}
// Клиент передаёт только id тарифа: сумму считает бэк по тому же правилу, что и
// витрина, и подписывает её внутри ссылки. Прислать свою цену нельзя.
// `url` — платёжная форма Продамуса; открывать её надо во ВНЕШНЕМ браузере
// (openExternalLink из tg.js), иначе на 3-D Secure пользователь застрянет.
// 503 — касса не настроена на бэке (нет ключа); 404 — тарифа нет или он снят.
export async function createPayment(tariffId, months = 1) {
  return apiFetch('/api/v1/payments/prodamus', {
    method: 'POST',
    body: JSON.stringify({ tariff_id: tariffId, months }),
  });
}

// GET /api/v1/payments/{id} → {payment_id, status, is_paid, amount, currency,
//   tariff_id, tariff_code, tariff_title, created_at, paid_at}
// Опрашивается после возврата из браузера: платёж подтверждает вебхук от кассы, а не
// редирект, поэтому единственный способ узнать результат — спросить бэк.
// `is_paid` выставляется только когда доступ уже выдан, так что по нему можно сразу
// перечитывать getAccess(). Чужой платёж отвечает 404.
export async function getPaymentStatus(paymentId) {
  return apiFetch(`/api/v1/payments/${paymentId}`);
}

// ───── ТАРО ─────

// GET /api/v1/tarot/spheres → ["Отношения","Финансы","Работа","Психология"]
export async function fetchTarotSpheres() {
  return apiFetch('/api/v1/tarot/spheres');
}

// GET /api/v1/tarot/spreads?sphere= → [{id, sphere, title, description, questions, cards_per_question, position}]
export async function fetchTarotSpreads(sphere = null) {
  const q = sphere ? `?sphere=${encodeURIComponent(sphere)}&limit=100` : '?limit=100';
  return apiFetch(`/api/v1/tarot/spreads${q}`);
}

// GET /api/v1/tarot/spreads/{id}
export async function fetchTarotSpread(spreadId) {
  return apiFetch(`/api/v1/tarot/spreads/${spreadId}`);
}

// POST /api/v1/tarot/spreads/{id}/select → якорит тему AI-чата, чистит контекст
export async function selectTarotSpread(spreadId) {
  return apiFetch(`/api/v1/tarot/spreads/${spreadId}/select`, { method: 'POST' });
}

// POST /api/v1/tarot/readings → { reading: {greeting, answers[], summary, advice}, drawn_cards[][], usage }
// Сам якорит тему чата — /select перед этим не нужен
// situation — обязательное описание ситуации от пользователя (текст до 3000 символов)
export async function generateTarotReading(spreadId, name, birthDate, situation) {
  return apiFetch('/api/v1/tarot/readings', {
    method: 'POST',
    timeoutMs: GENERATE_TIMEOUT_MS,
    body: JSON.stringify({
      spread_id: spreadId, name, birth_date: birthDate, situation,
    }),
  });
}

// ───── ПОСЛАНИЕ ДНЯ ─────

// GET /api/v1/daily/message → {message, card, card_name, card_reversed, issued_on, is_today} | null
// null — послание ещё ни разу не открывали. is_today=false означает, что сохранённое
// послание вчерашнее и сегодня можно вытянуть новое.
export async function getDailyMessage() {
  return apiFetch('/api/v1/daily/message');
}

// POST /api/v1/daily/message → то же тело
// Одно послание в сутки: если сегодняшнее уже вытянуто, бэк возвращает его же и
// повторно нейросеть не дёргает. Генерация долгая — таймаут как у раскладов.
export async function drawDailyMessage() {
  return apiFetch('/api/v1/daily/message', {
    method: 'POST',
    timeoutMs: GENERATE_TIMEOUT_MS,
  });
}

// ───── АСТРОЛОГИЯ ─────

// GET /api/v1/astro/sections → ["Предназначение", "Карма", ...]
export async function fetchAstroSections() {
  return apiFetch('/api/v1/astro/sections');
}

// Потолок limit на бэке — Query(ge=1, le=100), больше одной страницей не отдаст
const CATALOGUE_PAGE = 100;

// GET /api/v1/astro/readings?section= → [{id, section, title, description, points, position}]
// Каталог тянем страницами до упора: один запрос с limit=100 молча обрезал бы
// хвост, как только разборов станет больше сотни.
export async function fetchAstroReadings(section = null) {
  const base = section ? `?section=${encodeURIComponent(section)}&` : '?';
  const all = [];
  for (let offset = 0; ; offset += CATALOGUE_PAGE) {
    const page = await apiFetch(
      `/api/v1/astro/readings${base}limit=${CATALOGUE_PAGE}&offset=${offset}`,
    );
    all.push(...page);
    if (page.length < CATALOGUE_PAGE) return all;
  }
}

// GET /api/v1/astro/readings/{id}
export async function fetchAstroReading(readingId) {
  return apiFetch(`/api/v1/astro/readings/${readingId}`);
}

// POST /api/v1/astro/readings/{id}/select → якорит тему AI-чата, чистит контекст
export async function selectAstroReading(readingId) {
  return apiFetch(`/api/v1/astro/readings/${readingId}/select`, { method: 'POST' });
}

// POST /api/v1/astro/readings → { reading: {greeting, points[], summary}, chart[],
//   usage, history_id }
// Сам якорит тему чата — /select перед этим не нужен.
// history_id — id разбора в «Моих разборах»: бэк сохраняет его сам, клиенту остаётся
// только показать по нему кнопку «прислать PDF» (см. блок «МОИ РАЗБОРЫ» ниже).
const normTime = (t) => (t.split(':').length === 2 ? `${t}:00` : t);

// partner — опциональный второй человек ({name, birthDate, birthTime, birthCity}),
// используется в разделе «Совместимость».
export async function generateAstroReading(readingId, name, birthDate, birthTime, birthCity, partner) {
  const body = {
    reading_id: readingId,
    name,
    birth_date: birthDate,
    birth_time: normTime(birthTime),
    birth_city: birthCity,
  };
  if (partner) {
    body.partner_name = partner.name;
    body.partner_birth_date = partner.birthDate;
    body.partner_birth_time = normTime(partner.birthTime);
    body.partner_birth_city = partner.birthCity;
  }
  return apiFetch('/api/v1/astro/readings', {
    method: 'POST',
    timeoutMs: GENERATE_TIMEOUT_MS,
    body: JSON.stringify(body),
  });
}

// ───── МОИ РАЗБОРЫ (история) ─────

// Каждый удачный POST /astro/readings бэкенд дописывает в историю и возвращает её
// id в поле history_id ответа — то есть «сохранить разбор» на клиенте делать нечего,
// он уже сохранён. Раньше готовый разбор жил только в localStorage и в контексте
// чата: смена темы, чужой телефон или очистка кэша — и оплаченного разбора нет.

// GET /api/v1/astro/history?section=&limit=&offset= → [{id, reading_id, section,
//   title, name, birth_date, points_count, has_partner, export_count, created_at}]
// Без текстов: список показывает карточки, а сам разбор тянется по тапу.
export async function fetchAstroHistory(section = null, limit = 50, offset = 0) {
  const q = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  if (section) q.set('section', section);
  return apiFetch(`/api/v1/astro/history?${q}`);
}

// GET /api/v1/astro/history/{id} → тот же разбор целиком: chart, greeting,
// points[], summary и данные рождения, на которых он считался.
export async function fetchAstroHistoryEntry(entryId) {
  return apiFetch(`/api/v1/astro/history/${entryId}`);
}

// POST /api/v1/astro/history/{id}/send → {ok, filename, reason}
// Присылает разбор PDF-файлом в личку с ботом (скачивание внутри Telegram-вебвью на
// телефоне работает непредсказуемо, а файл в переписке открывается сразу и никуда
// не денется). ok=false — не сбой сети, а объяснимый отказ Telegram: показываем
// reason как есть (чаще всего «нажми /start у бота»).
// Сборка PDF и заливка файла долгие — таймаут как у генерации.
export async function sendAstroHistoryPdf(entryId) {
  return apiFetch(`/api/v1/astro/history/${entryId}/send`, {
    method: 'POST',
    timeoutMs: GENERATE_TIMEOUT_MS,
  });
}

// DELETE /api/v1/astro/history/{id} → 204. Квоту пакета не возвращает.
export async function deleteAstroHistoryEntry(entryId) {
  return apiFetch(`/api/v1/astro/history/${entryId}`, { method: 'DELETE' });
}

// POST /api/v1/astro/history/{id}/chat → Selection {module, ref_id, title, subtitle}
// Наводит чат с ИИ на сохранённый разбор: бэк заново собирает якорь (формула души,
// все пункты, итог) из БД и кладёт его в контекст Redis. После этого в чате Clavis
// можно спрашивать по этому разбору что угодно — хоть через полгода после генерации.
// Прошлая переписка при этом стирается: тема меняется. 409 — шаблон разбора убрали
// из каталога (читать и скачивать разбор всё ещё можно, обсуждать — нет).
export async function chatAboutAstroHistoryEntry(entryId) {
  return apiFetch(`/api/v1/astro/history/${entryId}/chat`, { method: 'POST' });
}

// ───── МОИ РАСКЛАДЫ ТАРО ─────

// GET /api/v1/tarot/history?limit= → [{spread_id, title, sphere, times, last_at,
//   last_reading_at, is_active}]
// Это список выбранных тем. Завершённые расклады с картами и толкованиями
// читаются отдельно через /tarot/saved ниже.
export async function fetchTarotHistory(limit = 20) {
  return apiFetch(`/api/v1/tarot/history?limit=${limit}`);
}

// Завершённые расклады: все выпавшие карты и толкования живут в БД и доступны
// через профиль даже после переключения темы чата.
export const fetchSavedTarotReadings = (limit = 50, offset = 0) =>
  apiFetch(`/api/v1/tarot/saved?limit=${limit}&offset=${offset}`);
export const fetchSavedTarotReading = (id) => apiFetch(`/api/v1/tarot/saved/${id}`);
export const deleteSavedTarotReading = (id) => apiFetch(`/api/v1/tarot/saved/${id}`, { method: 'DELETE' });
export const chatAboutSavedTarotReading = (id) => apiFetch(`/api/v1/tarot/saved/${id}/chat`, { method: 'POST' });

// ───── AI-ЧАТ ─────

// GET /api/v1/ai/session → {module, ref_id, title, subtitle, has_reading} | null
// has_reading — подставлен ли в тему готовый расклад/разбор (в Redis рядом с темой
// лежит якорь: карты или формула души с уже выданным толкованием). Шапка чата
// показывает это отдельно: «обсуждаем твой разбор» и «тема выбрана, разбора ещё не
// было» — для пользователя разные состояния.
export async function getAiSession() {
  return apiFetch('/api/v1/ai/session');
}

// GET /api/v1/ai/history → { messages: [{role: 'user'|'assistant', content}] }
// История только текущей темы — сервер стирает её при каждой смене темы/новом раскладе.
export async function getAiHistory() {
  return apiFetch('/api/v1/ai/history');
}

// У бэкенда нет DELETE /ai/history (у /history только GET → 405). Очистка
// диалога делается через DELETE /ai/session (end_session сбрасывает контекст).
export async function clearAiHistory() {
  return apiFetch('/api/v1/ai/session', { method: 'DELETE' });
}

// DELETE /api/v1/ai/session → выход из темы, контекст стирается
export async function endAiSession() {
  return apiFetch('/api/v1/ai/session', { method: 'DELETE' });
}

// POST /api/v1/ai/session/general → включить свободный чат (без расклада/разбора),
// возвращает Selection {module: 'general', ...}; контекст изолирован, как у любой темы
export async function startGeneralChat() {
  return apiFetch('/api/v1/ai/session/general', { method: 'POST' });
}

export const startCardsChat = (topic = null) => apiFetch('/api/v1/ai/session/cards', {
  method: 'POST', body: JSON.stringify({ topic }),
});
export const startDreamChat = () => apiFetch('/api/v1/ai/session/dream', { method: 'POST' });

// POST /api/v1/ai/chat → { reply, selection, usage, balance: {free, paid, total, by_scope} }
// balance — остаток ПОСЛЕ этого сообщения, его и показываем под полем ввода.
// 409 = тема не выбрана — сначала /select или генерация расклада/разбора.
// 402 = нет доступа к теме или кончились вопросы (см. ACCESS_CODES).
export async function sendAiChat(prompt) {
  return apiFetch('/api/v1/ai/chat', {
    method: 'POST',
    timeoutMs: GENERATE_TIMEOUT_MS,
    body: JSON.stringify({ prompt }),
  });
}
